import { useNavigate } from 'react-router-dom'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/common/ui/alert'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Info, Wallet, Gift, ArrowRight, ExternalLink } from 'lucide-react'

export default function DefaultProviderVersionCard() {
	const navigate = useNavigate()

	return (
		<div className="space-y-4">
			{/* System Overview Alert */}
			<Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
				<Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
				<AlertTitle className="text-base text-blue-900 dark:text-blue-100">
					การตั้งค่าเวอร์ชันค่ายเกม (เวอร์ชั่นใหม่ vs เวอร์ชั่นเริ่มต้น)
				</AlertTitle>
				<AlertDescription className="mt-2 space-y-2 text-sm text-blue-800 dark:text-blue-200">
					<div className="flex items-start gap-2">
						<div className="flex-1 space-y-1">
							<p>
								<Badge
									variant="outline"
									className="font-semibold text-blue-600 dark:text-blue-400"
								>
									เวอร์ชั่นใหม่
								</Badge>{' '}
								= ระบบค่ายเกมของทางเราที่สามารถตั้งค่าการปรับแตกได้
							</p>
							<p>
								<Badge
									variant="outline"
									className="font-semibold text-green-600 dark:text-green-400"
								>
									เวอร์ชั่นเริ่มต้น
								</Badge>{' '}
								= ค่ายเกมจากผู้ให้บริการ (ไม่สามารถปรับแตกได้)
							</p>
						</div>
					</div>

					<div className="space-y-2 rounded-lg border border-orange-300 bg-orange-50/50 p-3 dark:border-orange-700 dark:bg-orange-950/30">
						<p className="text-xs font-semibold text-orange-900 dark:text-orange-100">
							📋 การตั้งค่าเลือกค่ายจะมีการตั้งค่าหลักๆ ทั้งหมด 3 จุด:
						</p>
						<div className="space-y-1.5 text-xs text-orange-800 dark:text-orange-200">
							<p className="flex items-start gap-2">
								<span className="font-semibold">1. ฝากไม่รับโปร:</span>
								<span>
									ตั้งค่าได้ที่หน้า{' '}
									<Button
										variant="link"
										className="h-auto p-0 text-xs underline"
										onClick={() => navigate('/operator/main-wallet-setting')}
									>
										การตั้งค่าฝากไม่รับโปร
									</Button>
								</span>
							</p>
							<p className="flex items-start gap-2">
								<span className="font-semibold">2. เงินติดเงื่อนไข:</span>
								<span>สามารถตั้งได้ในตอนสร้างโปรโมชั่น</span>
							</p>
							<p className="flex flex-col gap-1">
								<span className="font-semibold">3. กระเป๋าอีเวนท์ต่างๆ:</span>
								[วงล้อ, คืนยอดเสีย, คอมมิชชั่น, เช็คอินรายวัน]
								สามารถตั้งได้ในตอนสร้างอีเวนท์ต่างๆ
							</p>
						</div>
					</div>

					<div className="rounded-lg border border-blue-300 bg-white/50 p-2 dark:border-blue-700 dark:bg-blue-900/30">
						<p className="text-xs font-medium">
							⚙️ ระบบใช้ เวอร์ชั่นใหม่ เป็นค่าเริ่มต้นเสมอ • ต้องตั้งค่าทั้ง 3
							จุดข้างต้นหากต้องการให้ผู้เล่นใช้เวอร์ชั่นเริ่มต้น
						</p>
					</div>
				</AlertDescription>
			</Alert>

			{/* Configuration Cards */}
			<div className="grid gap-4 md:grid-cols-3">
				{/* 1. Main Wallet */}
				<Card className="border-blue-200 dark:border-blue-800">
					<CardHeader className="pb-3">
						<div className="flex items-center gap-2">
							<Wallet className="h-4 w-4 text-blue-600 dark:text-blue-400" />
							<CardTitle className="text-base">1. ฝากไม่รับโปร</CardTitle>
						</div>
						<CardDescription className="text-xs">
							ตั้งค่าเวอร์ชันค่ายเกมสำหรับผู้เล่นที่เล่นโดยไม่ใช้โปร
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-2 rounded-lg border border-dashed bg-blue-50/50 p-3 dark:bg-blue-950/30">
							<p className="text-xs font-medium text-blue-900 dark:text-blue-100">
								📌 ตั้งค่า PG, Joker, JILI เวอร์ชั่นใหม่/เริ่มต้น
							</p>
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate('/operator/main-wallet-setting')}
								className="h-8 w-full gap-2 text-xs"
							>
								ไปที่การตั้งค่าฝากไม่รับโปร
								<ExternalLink className="h-3 w-3" />
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* 2. Deposit Promotion */}
				<Card className="border-green-200 dark:border-green-800">
					<CardHeader className="pb-3">
						<div className="flex items-center gap-2">
							<Gift className="h-4 w-4 text-green-600 dark:text-green-400" />
							<CardTitle className="text-base">2. โปรโมชั่นฝาก</CardTitle>
						</div>
						<CardDescription className="text-xs">
							ตั้งค่าเวอร์ชันค่ายเกมสำหรับแต่ละโปรโมชั่น
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-2 rounded-lg border border-dashed bg-green-50/50 p-3 dark:bg-green-950/30">
							<p className="text-xs font-medium text-green-900 dark:text-green-100">
								📌 ตั้งค่าตอนสร้าง/แก้ไขโปรโมชั่น
							</p>
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate('/operator/deposit-promotion')}
								className="h-8 w-full gap-2 text-xs"
							>
								ไปที่หน้าโปรโมชั่น
								<ExternalLink className="h-3 w-3" />
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* 3. Wallet Condition */}
				<Card className="border-purple-200 dark:border-purple-800">
					<CardHeader className="pb-3">
						<div className="flex items-center gap-2">
							<ArrowRight className="h-4 w-4 text-purple-600 dark:text-purple-400" />
							<CardTitle className="text-base">3. กระเป๋าอีเวนท์</CardTitle>
						</div>
						<CardDescription className="text-xs">
							ตั้งค่าเวอร์ชันค่ายเกมสำหรับแต่ละอีเวนท์
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							<p className="text-xs font-medium text-purple-900 dark:text-purple-100">
								📌 ตั้งค่าตอนสร้าง/แก้ไขอีเวนท์
							</p>
							<div className="grid grid-cols-2 gap-1.5">
								<div className="rounded-lg border border-dashed bg-muted/30 p-1.5">
									<p className="text-xs font-medium">คอมมิชชั่น</p>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => navigate('/operator/commission')}
										className="mt-0.5 h-6 w-full justify-start gap-2 px-2 text-xs"
									>
										ตั้งค่า
										<ExternalLink className="h-3 w-3" />
									</Button>
								</div>
								<div className="rounded-lg border border-dashed bg-muted/30 p-1.5">
									<p className="text-xs font-medium">คืนยอดเสีย</p>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => navigate('/operator/return-loss')}
										className="mt-0.5 h-6 w-full justify-start gap-2 px-2 text-xs"
									>
										ตั้งค่า
										<ExternalLink className="h-3 w-3" />
									</Button>
								</div>
								<div className="rounded-lg border border-dashed bg-muted/30 p-1.5">
									<p className="text-xs font-medium">เช็คอินรายวัน</p>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => navigate('/operator/daily-check-in')}
										className="mt-0.5 h-6 w-full justify-start gap-2 px-2 text-xs"
									>
										ตั้งค่า
										<ExternalLink className="h-3 w-3" />
									</Button>
								</div>
								<div className="rounded-lg border border-dashed bg-muted/30 p-1.5">
									<p className="text-xs font-medium">วงล้อโชคดี</p>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => navigate('/operator/lucky-wheel')}
										className="mt-0.5 h-6 w-full justify-start gap-2 px-2 text-xs"
									>
										ตั้งค่า
										<ExternalLink className="h-3 w-3" />
									</Button>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	)
}
